'use client';

import { useState, useEffect } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import type { Timeframe, SportFilter, BetTypeFilter } from './ReportingFilters';

interface MonthData {
  month: string;
  units: number;
  roi: number;
  wins: number;
  losses: number;
  pushes: number;
}

interface MonthlyPLChartProps {
  timeframe: Timeframe;
  sport: SportFilter;
  betType: BetTypeFilter;
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{name: string; value: number; color: string}>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 shadow-lg min-w-[180px]">
      <p className="text-xs font-semibold text-[hsl(var(--foreground))] mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-xs text-[hsl(var(--foreground-muted))]">{entry.name}</span>
          </div>
          <span className={`font-mono text-xs font-semibold tabular-nums ${entry.name === 'Units' ? (entry.value >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400') : 'text-[hsl(var(--foreground))]'}`}>
            {entry.name === 'Units' ? (entry.value >= 0 ? '+' : '') : ''}{entry.value}{entry.name === 'Units' ? 'u' : entry.name === 'ROI' ? '%' : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function getDateFilter(timeframe: Timeframe): string | null {
  const now = new Date();
  if (timeframe === '7d') { now.setDate(now.getDate() - 7); return now.toISOString(); }
  if (timeframe === '30d') { now.setDate(now.getDate() - 30); return now.toISOString(); }
  if (timeframe === '90d') { now.setDate(now.getDate() - 90); return now.toISOString(); }
  if (timeframe === 'ytd') { return new Date(now.getFullYear(), 0, 1).toISOString(); }
  return null;
}

export default function MonthlyPLChart({ timeframe, sport, betType }: MonthlyPLChartProps) {
  const [monthlyData, setMonthlyData] = useState<MonthData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('posted_at, units, units_result, result')
          .neq('result', 'pending')
          .order('posted_at', { ascending: true });

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);
        const dateFrom = getDateFilter(timeframe);
        if (dateFrom) query = query.gte('posted_at', dateFrom);

        const { data, error } = await query;
        if (error) throw error;

        const byMonth: Record<string, { units: number; risked: number; wins: number; losses: number; pushes: number }> = {};
        (data || []).forEach((p) => {
          const month = new Date(p.posted_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          if (!byMonth[month]) byMonth[month] = { units: 0, risked: 0, wins: 0, losses: 0, pushes: 0 };
          byMonth[month].units += p.units_result || 0;
          byMonth[month].risked += p.units || 0;
          if (p.result === 'win') byMonth[month].wins++;
          else if (p.result === 'loss') byMonth[month].losses++;
          else if (p.result === 'push') byMonth[month].pushes++;
        });

        const points: MonthData[] = Object.entries(byMonth).map(([month, d]) => ({
          month,
          units: parseFloat(d.units.toFixed(2)),
          roi: d.risked > 0 ? parseFloat(((d.units / d.risked) * 100).toFixed(1)) : 0,
          wins: d.wins,
          losses: d.losses,
          pushes: d.pushes,
        }));

        setMonthlyData(points);
      } catch {
        setMonthlyData([]);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [timeframe, sport, betType]);

  return (
    <div className="stat-card mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Monthly P&L</h2>
          <p className="text-xs text-[hsl(var(--foreground-muted))] mt-0.5">Units won/lost per month with ROI overlay</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-[hsl(var(--foreground-muted))]">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-[hsl(var(--primary)/0.7)]" />
            <span>Units</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-amber-400" />
            <span>ROI %</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[260px] gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading chart...</span>
        </div>
      ) : monthlyData.length === 0 ? (
        <div className="flex items-center justify-center h-[260px]">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No data yet. Chart will appear once picks are graded.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={monthlyData} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 13%, 90%)" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="units" tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}u`} />
            <YAxis yAxisId="roi" orientation="right" tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
            <ReferenceLine yAxisId="units" y={0} stroke="hsl(220, 13%, 82%)" strokeDasharray="4 4" />
            <Tooltip content={<CustomTooltip />} />
            <Bar yAxisId="units" dataKey="units" name="Units" fill="hsl(155, 65%, 38%)" radius={[4, 4, 0, 0]} fillOpacity={0.8} />
            <Line yAxisId="roi" type="monotone" dataKey="roi" name="ROI" stroke="hsl(38, 85%, 45%)" strokeWidth={2} dot={{ r: 3, fill: 'hsl(38, 85%, 45%)', stroke: 'white', strokeWidth: 2 }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}